import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import Layer from '../ui/Layer';
import Toast, { type ToastTone } from '../ui/Toast';

/** How long a notice stays, in milliseconds. An error stays until it is closed. */
const DEFAULT_MS = 4000;
/** More than this many at once and the oldest leaves (errors last). */
const MAX_VISIBLE = 4;

export interface NoticeInput {
  tone?: ToastTone;
  /** A few words in bold before the message, such as "Executed." */
  title?: string;
  /** Plain text. It is never read as HTML. */
  message: string;
  /** Overrides the default stay; `null` keeps the notice until it is closed. */
  durationMs?: number | null;
}

interface NoticeItem extends Required<Pick<NoticeInput, 'tone' | 'message'>> {
  id: number;
  title?: string;
  durationMs: number | null;
}

interface ToastApi {
  /** Shows a notice. A bare string is an informational one. Returns its id. */
  notify: (notice: string | NoticeInput) => number;
  dismiss: (id: number) => void;
}

interface ToastState extends ToastApi {
  items: NoticeItem[];
}

const ToastContext = createContext<ToastState | null>(null);

/** Holds the notices of the signed-in app (item 74). Draw them with `ToastRegion`. */
export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<NoticeItem[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setItems((current) => current.filter((item) => item.id !== id));
  }, []);

  const notify = useCallback((notice: string | NoticeInput) => {
    const input: NoticeInput = typeof notice === 'string' ? { message: notice } : notice;
    const tone = input.tone ?? 'info';
    const id = nextId.current++;
    const durationMs = input.durationMs !== undefined ? input.durationMs : tone === 'error' ? null : DEFAULT_MS;
    setItems((current) => {
      const next = [...current, { id, tone, title: input.title, message: input.message, durationMs }];
      // Past the limit the oldest notice leaves, but an error nobody has closed is kept while anything else can go.
      while (next.length > MAX_VISIBLE) {
        const at = next.slice(0, -1).findIndex((item) => item.tone !== 'error');
        next.splice(at === -1 ? 0 : at, 1);
      }
      return next;
    });
    return id;
  }, []);

  const value = useMemo(() => ({ items, notify, dismiss }), [items, notify, dismiss]);
  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
};

/** Shows a notice from any component of the signed-in app. */
export function useToast(): ToastApi {
  const state = useContext(ToastContext);
  if (!state) throw new Error('useToast needs a ToastProvider above it');
  return state;
}

/**
 * Draws the notices, bottom right, above dialogs. Render it once, inside the element that carries the
 * theme (`.c3-app`), so the notices follow Light and Dim. The region exists while it is empty, so a
 * screen reader hears the first notice.
 */
export const ToastRegion: React.FC = () => {
  const state = useContext(ToastContext);
  if (!state) throw new Error('ToastRegion needs a ToastProvider above it');
  return (
    <Layer>
      <div className="ui-toast-region" role="region" aria-label="Notices">
        {state.items.map((item) => (
          <Toast key={item.id} {...item} onClose={state.dismiss} />
        ))}
      </div>
    </Layer>
  );
};
